// // src/pages/Auth/ResetPassword.jsx

import { useEffect, useState ,useMemo} from "react";
import { Box, CircularProgress } from "@mui/material";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from "../../../components/Cards/Card";
import CustomPasswordInput from "../../../components/PasswordInput";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useFormik } from "formik";
import { toast, Toaster } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../components/BaseUrl";




const RuleRow = ({ ok, label, showError }) => {
  const textColor = ok ? "#16a34a" : showError ? "#d32f2f" : "#64748b";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: ok ? "none" : "2px solid #9aa4b2",
          background: ok ? "#22c55e" : "transparent",
        }}
      >
        {ok ? (
          <span
            style={{
              width: 6,
              height: 10,
              borderRight: "2px solid #fff",
              borderBottom: "2px solid #fff",
              transform: "rotate(45deg)",
            }}
          />
        ) : (
          <span style={{ position: "relative", width: 10, height: 10 }}>
            <span
              style={{
                position: "absolute",
                width: "100%",
                height: 2,
                background: "#9aa4b2",
                transform: "rotate(45deg)",
                top: "50%",
                left: 0,
                marginTop: -1,
              }}
            />
            <span
              style={{
                position: "absolute",
                width: "100%",
                height: 2,
                background: "#9aa4b2",
                transform: "rotate(-45deg)",
                top: "50%",
                left: 0,
                marginTop: -1,
              }}
            />
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: textColor }}>{label}</span>
    </Box>
  );
};

const PasswordChecklist = ({ password, showError }) => {
    const { t } = useTranslation();
  const rules = useMemo(
    () => ({
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
    }),
    [password]
  );

  return (
  <Box sx={{ mt: 1, mb: { xs: 2, sm: 1.5 } }}>
  <RuleRow ok={rules.length} label={t('passwordRules.minLength')} showError={showError && !rules.length}/>
  <RuleRow ok={rules.upper} label={t('passwordRules.upperCase')} showError={showError && !rules.upper}/>
  <RuleRow ok={rules.lower} label={t('passwordRules.lowerCase')} showError={showError && !rules.lower}/>
  <RuleRow ok={rules.number} label={t('passwordRules.number')} showError={showError && !rules.number}/>
  <RuleRow ok={rules.special} label={t('passwordRules.special')} showError={showError && !rules.special}/>
</Box>
  );
};
const ResetPassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation(); // ✅ fix: define location

  const { email, otp, resetToken } = location.state || {}; // ✅ these come from Verification

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!email || !otp) {
      toast.error("Missing email or OTP. Please restart the process.");
      navigate("/forget-password");
    }
  }, [email, otp, navigate]);

  const validate = (values) => {
    const errors = {};
    if (!values.password) {
      errors.password = t("resetPassword.passwordRequired");
    } else if (values.password.length < 8) {
      errors.password = t("resetPassword.passwordTooShort");
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = t("resetPassword.confirmPasswordRequired");
    } else if (values.confirmPassword !== values.password) {
      errors.confirmPassword = t("resetPassword.passwordsMismatch");
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validate,
    onSubmit: async (values) => {
      const payload = {
        email,
        verificationCode: otp, // ✅ use OTP passed from Verification screen
        newPassword: values.password,
        confirmPassword: values.confirmPassword,
      };

      console.groupCollapsed("[ResetPassword] Payload");
      console.table(payload);
      console.groupEnd();

      setIsSubmitting(true);

      try {
        const res = await fetch(`${BASE_URL}/company-admins/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok || data?.error) {
          throw new Error(data?.message || "Failed to reset password");
        }

        toast.success(data?.message || "Password reset successful");
        navigate("/"); // ✅ redirect to login
      } catch (err) {
        console.error("[ResetPassword] Submit error:", err);
        toast.error(err.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <Box sx={{ backgroundColor: "#EFF4F8", height: "100vh" }}>
      <Toaster />
      <Box sx={{ py: isMobile ? 5 : 10, px: 2, display: "flex", justifyContent: "center" }}>
        <BaseCard maxWidth={isMobile ? "100%" : 380}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
                  {t("resetPassword.title")}
                </CustomText>
                <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary }}>
                  {t("resetPassword.subtitle")}
                </CustomText>
              </Box>

              {/* Password Input */}
              <label>
                {t("resetPassword.passwordLabel")}<span style={{ color: "red" }}> *</span>
              </label>
              <CustomPasswordInput
                name="password"
                placeholder='********'
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && formik.errors.password}
                initiallyVisible={true}
              />
              {formik.touched.password && formik.errors.password && (
                <span style={{ color: "red", fontSize: 12 }}>{formik.errors.password}</span>
              )}

   <PasswordChecklist password={formik.values.password} showError={formik.touched.password} />
              {/* Confirm Password */}
              <label style={{ marginTop: 5 }}>
                {t("resetPassword.confirmPasswordLabel")}<span style={{ color: "red" }}> *</span>
              </label>
              <CustomPasswordInput
                name="confirmPassword"
                value={formik.values.confirmPassword}
             placeholder='********'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && formik.errors.confirmPassword}
                initiallyVisible={true}
              />
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <span style={{ color: "red", fontSize: 12 }}>{formik.errors.confirmPassword}</span>
              )}

              {/* Submit Button */}
              <CustomButton
                label={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {isSubmitting && <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />}
                    {t("resetPassword.submitButton")}
                  </Box>
                }
                type="submit"
                fullWidth
                sx={{ mt: 2 }}
              />
            </Box>
          </form>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default ResetPassword;




